/**
 * Host side of the arcade bridge protocol. The contract lives in the svara-arcade repo
 * (`packages/arcade-core/src/bridge/messages.ts`, docs/HOST_BRIDGE.md); this file mirrors
 * the envelope and the message types the app has to understand. Keep both in sync.
 */
export const ARCADE_BRIDGE_CHANNEL = 'svara-arcade';
export const ARCADE_BRIDGE_VERSION = 1;

export interface ArcadeInitData {
	token: string;
	apiUrl: string;
	casinoId: string;
	playerId: string;
	language: string;
	translations: Record<string, string>;
	soundEnabled: boolean;
	platform: 'web' | 'android' | 'ios';
	/** App version, stamped as `app_version` on analytics. */
	appVersion?: string;
	/** `sessionId` is the app's Helix session, when it has one; the game never invents one. */
	analytics?: { deltaDna: boolean; helix: boolean; sessionId?: string };
}

export type ArcadeGameToHostMessage =
	| { type: 'ARCADE_READY'; payload: { game: string; version: string } }
	| { type: 'ARCADE_SHOWN'; payload: Record<string, never> }
	| { type: 'ARCADE_CLOSE'; payload: { reason: 'user' | 'error' | 'host' } }
	| { type: 'ARCADE_BALANCE'; payload: { chips: number; tickets: number } }
	| { type: 'ARCADE_NOT_ENOUGH_CHIPS'; payload: { required: number } }
	| { type: 'ARCADE_BECOME_VIP'; payload: Record<string, never> }
	| { type: 'ARCADE_OPEN_STORE'; payload: Record<string, never> }
	| { type: 'ARCADE_TOKEN_EXPIRED'; payload: Record<string, never> }
	| {
			type: 'ARCADE_UI_EVENT';
			payload: { name: string; params: Record<string, string | number | boolean> };
	  }
	| { type: 'ARCADE_ERROR'; payload: { message: string; fatal: boolean } };

export type ArcadeHostToGameMessage =
	| { type: 'ARCADE_INIT'; payload: ArcadeInitData }
	| { type: 'ARCADE_TOKEN'; payload: { token: string } }
	| { type: 'ARCADE_SOUND'; payload: { enabled: boolean } }
	| { type: 'ARCADE_DESTROY'; payload: Record<string, never> };

export interface ArcadeEnvelope<T extends { type: string; payload: unknown }> {
	channel: typeof ARCADE_BRIDGE_CHANNEL;
	v: typeof ARCADE_BRIDGE_VERSION;
	type: T['type'];
	payload: T['payload'];
}

export function arcadeEnvelope<T extends ArcadeHostToGameMessage>(message: T): ArcadeEnvelope<T> {
	return {
		channel: ARCADE_BRIDGE_CHANNEL,
		v: ARCADE_BRIDGE_VERSION,
		type: message.type,
		payload: message.payload,
	};
}

export function parseArcadeMessage(data: unknown): ArcadeGameToHostMessage | null {
	let value: unknown = data;
	if (typeof value === 'string') {
		try {
			value = JSON.parse(value);
		} catch {
			return null;
		}
	}
	if (typeof value !== 'object' || value === null) return null;
	const record = value as Record<string, unknown>;
	if (record.channel !== ARCADE_BRIDGE_CHANNEL || record.v !== ARCADE_BRIDGE_VERSION) return null;
	if (typeof record.type !== 'string' || !record.type.startsWith('ARCADE_')) return null;
	return { type: record.type, payload: record.payload ?? {} } as ArcadeGameToHostMessage;
}

/**
 * JavaScript the native WebView runs before the game loads. Nothing here carries data:
 * the game asks for its init through ARCADE_READY and the host answers by calling
 * `window.__ARCADE_HOST__.deliver(json)` (installed by the game's HostBridge), so no
 * token ever appears in a URL or in the page source.
 */
export const ARCADE_NATIVE_PRELUDE = `
(function () {
  var post = function (type, data) {
    try {
      if (window.ReactNativeWebView) {
        window.ReactNativeWebView.postMessage(JSON.stringify({ type: type, data: data }));
      }
    } catch (e) {}
  };
  var originalError = console.error;
  console.error = function () {
    originalError.apply(console, arguments);
    post('console.error', Array.prototype.slice.call(arguments).map(String));
  };
  window.addEventListener('error', function (e) {
    post('error', { message: e && e.message, filename: e && e.filename, line: e && e.lineno });
  });
})();
true;
`;

/** Injects a host -> game message into the native WebView. */
export function nativeDeliverScript(message: ArcadeHostToGameMessage): string {
	const json = JSON.stringify(JSON.stringify(arcadeEnvelope(message)));
	return `try { window.__ARCADE_HOST__ && window.__ARCADE_HOST__.deliver(${json}); } catch (e) {} true;`;
}
